import React from "react";
import styled, { css } from "styled-components";
import { getImage, getSrc, GatsbyImage } from "gatsby-plugin-image";
import Box from "react-raster";
import { Media } from "../Media";
import Container from "../Container";
import ButtonLink from "../ButtonLink";
import { H3, AccentTitle, Blockquote, Eyebrow, Copy } from "../Typography";

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 261px;
  background-repeat: no-repeat;
  background-size: cover;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  padding-bottom: ${({ $hasBottomMargin }) =>
    ($hasBottomMargin && "60px") || "20px"};
  @media (${({ theme }) => theme.mq.m}) {
    min-height: 261px;
  }

  ${({ $image, $imageMask }) =>
    $image
      ? $imageMask
        ? css`
            background-image: linear-gradient(
                rgba(0, 0, 0, 0.5),
                rgba(0, 0, 0, 0.5)
              ),
              url(${$image});
          `
        : css`
            background-image: url(${$image});
          `
      : css`
          background-color: transparent;
        `}

  ${({ $contentAlignment }) =>
    $contentAlignment === "left"
      ? css`
          align-items: flex-start;
        `
      : css`
          align-items: center;
        `}
`;

const Description = styled(Copy)`
  text-align: ${({ $contentAlignment }) =>
    $contentAlignment === "left" ? "left" : "center"};
`;

const ImageOverlayWrapper = styled.div`
  position: relative;
  padding-bottom: ${({ $hasBottomMargin }) =>
    ($hasBottomMargin && "60px") || "20px"};
`;

const ImageOverlay = styled(GatsbyImage)`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  @media (${({ theme }) => theme.mq.l}) {
    display: block;
    transition: transform 0.3s ease-in;

    @media (prefers-reduced-motion) {
      transform: translateX(0);
      transition: none;
    }
  }
`;

const QuoteContainer = styled.div`
  max-width: 614px;
  margin-top: 90px;

  @media (${({ theme }) => theme.mq.m}) {
    position: absolute;
    bottom: 50px;
    left: 50px;
    margin-top: 0;
  }
`;

const MobileBlockquoteContainer = styled.div`
  margin-left: 30px;
`;

function BigImage({
  image,
  title,
  contentAlignment,
  ctaUrl,
  ctaLabel,
  hasBottomMargin,
  imageOverlay,
  components,
  layout,
  imageMask,
}) {
  const contentCols = contentAlignment === "left" ? [6, 10, 10, 8] : [6, 12];
  const contentLeftSpacing = contentAlignment === "left" ? [0] : [0];
  const contentRightSpacing = contentAlignment === "left" ? [0, 2] : [0];
  const imageOverlayImage = getImage(imageOverlay);
  const hasPageWidth = layout === "page width";

  const pageWidthStyles = hasPageWidth
    ? {
        width: "100%",
        backgroundImage: imageMask
          ? `linear-gradient(rgba(0, 0, 0, 0.5),rgba(0, 0, 0, 0.5)), url(${getSrc(
              image
            )})`
          : ` url(${getSrc(image)})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        paddingBottom: "30px",
      }
    : undefined;

  if (title || ctaUrl) {
    return (
      <Wrapper
        $image={!hasPageWidth && getSrc(image)}
        $imageMask={imageMask}
        $hasBottomMargin={hasBottomMargin}
        $contentAlignment={contentAlignment}
      >
        <Container style={pageWidthStyles}>
          <Box
            cols={contentCols}
            marginLeft={contentLeftSpacing}
            marginRight={contentRightSpacing}
            paddingTop={"30px"}
            display="flex"
            flexDirection="column"
            alignItems={contentAlignment === "left" ? "left" : "center"}
          >
            {title && (
              <H3 $colour="white" $contentAlignment={contentAlignment}>
                {title}
              </H3>
            )}
            {components?.description && (
              <Description
                colour="white"
                medium
                $contentAlignment={contentAlignment}
              >
                {components.description}
              </Description>
            )}
            {ctaUrl && (
              <ButtonLink isWhite={false} label={ctaLabel} url={ctaUrl} />
            )}
          </Box>
        </Container>
      </Wrapper>
    );
  }

  if (imageOverlayImage || components?.quote) {
    return (
      <>
        <ImageOverlayWrapper $hasBottomMargin={hasBottomMargin}>
          <GatsbyImage image={getImage(image)} alt={title || ""} />
          {imageOverlayImage && (
            <ImageOverlay
              role="presentation"
              image={imageOverlayImage}
              alt={title || ""}
            />
          )}
          {components?.quote && (
            <Media greaterThanOrEqual="m">
              <QuoteContainer>
                <Blockquote colour="white">{components.quote}</Blockquote>
                <AccentTitle colour="white">{components?.name}</AccentTitle>
                <Eyebrow colour="white">{components?.company}</Eyebrow>
              </QuoteContainer>
            </Media>
          )}
        </ImageOverlayWrapper>
        {components?.quote && (
          <Media lessThan="m">
            <Container>
              <Box cols={[5, 10]} marginLeft={1}>
                <MobileBlockquoteContainer>
                  <Blockquote>{components.quote}</Blockquote>
                  <AccentTitle>{components?.name}</AccentTitle>
                  <Eyebrow colour="lightGrey">{components?.company}</Eyebrow>
                </MobileBlockquoteContainer>
              </Box>
            </Container>
          </Media>
        )}
      </>
    );
  }

  return (
    <GatsbyImage
      style={{ marginBottom: hasBottomMargin ? "100px" : undefined }}
      image={getImage(image)}
      alt={title || ""}
    />
  );
}

export default BigImage;
